// The glass sky: the backdrop behind every screen (time-of-day gradients, soft colour pools, stars at night)
// and the home scene's sun or moon behind opaque 3D clouds that drift across. Pure SVG + CSS; decorative only.
import { useId } from 'react';

// A puffy cloud: overlapping balls lit from the upper left. Colours come from CSS (--c1..--c3, --cs) so night recolours it.
const PUFFS: [number, number, number][] = [[34, 60, 26], [68, 44, 34], [106, 50, 28], [132, 64, 20], [86, 70, 26], [50, 72, 20]];

function Cloud() {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 166 106" aria-hidden="true">
      <defs>
        <radialGradient id={`p${id}`} cx="38%" cy="30%" r="75%">
          <stop offset="0" style={{ stopColor: 'var(--c1)' }} />
          <stop offset="0.5" style={{ stopColor: 'var(--c2)' }} />
          <stop offset="1" style={{ stopColor: 'var(--c3)' }} />
        </radialGradient>
        <filter id={`s${id}`} x="-25%" y="-25%" width="150%" height="170%">
          <feDropShadow dx="0" dy="9" stdDeviation="8" style={{ floodColor: 'var(--cs)' }} floodOpacity="0.3" />
        </filter>
      </defs>
      <g filter={`url(#s${id})`}>
        {PUFFS.map(([cx, cy, r], i) => <circle key={i} cx={cx} cy={cy} r={r} fill={`url(#p${id})`} />)}
      </g>
    </svg>
  );
}

/** Clouds flying across the scene: each lane has its own height, size, speed and starting point. Fully opaque. */
const LANES = [
  { top: '4%', w: 84, dur: 46, delay: -12 },
  { top: '20%', w: 128, dur: 62, delay: -40 },
  { top: '56%', w: 112, dur: 54, delay: -8 },
  { top: '72%', w: 92, dur: 70, delay: -52 },
  { top: '36%', w: 64, dur: 40, delay: -26 },
];

export function FlyingClouds() {
  return (
    <div className="gl-clouds" aria-hidden="true">
      {LANES.map((l, i) => (
        <div key={i} className="gl-lane" style={{ ['--w' as string]: `${l.w}px`, top: l.top, animationDuration: `${l.dur}s`, animationDelay: `${l.delay}s` }}>
          <div className="gl-cloud" style={{ width: `${l.w}px` }}><Cloud /></div>
        </div>
      ))}
    </div>
  );
}

export function Moon({ className = 'gl-celest gl-moon' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 140 130" aria-hidden="true">
      <defs>
        <radialGradient id="gl-moon-fill" cx="35%" cy="30%" r="80%"><stop offset="0" stopColor="#FFFBEF" /><stop offset="0.6" stopColor="#FBEFD2" /><stop offset="1" stopColor="#EED7A6" /></radialGradient>
        <radialGradient id="gl-moon-glow" cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="#FFF6DD" stopOpacity="0.85" /><stop offset="1" stopColor="#FFF6DD" stopOpacity="0" /></radialGradient>
      </defs>
      <g transform="translate(-130 -8)">
        <circle cx="200" cy="70" r="66" fill="url(#gl-moon-glow)" />
        <path d="M226 18c-30-6-60 14-64 46-5 33 19 61 51 63 14 1 27-3 37-11-35 3-62-25-58-59 2-17 14-32 34-39Z" fill="url(#gl-moon-fill)" />
        <path d="M216 22c-24 2-44 22-46 47 0 4 0 8 1 12-6-28 14-54 45-59Z" fill="#fff" opacity="0.7" />
        <path d="M181 66c3 4 9 4 12 0" stroke="#8C6B3E" strokeWidth="2.6" strokeLinecap="round" fill="none" />
        <path d="M186 84c3 2 7 2 9 0" stroke="#8C6B3E" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <circle cx="177" cy="76" r="4" fill="#F5B9A6" opacity="0.55" />
      </g>
    </svg>
  );
}

export function Sun() {
  return (
    <svg className="gl-celest gl-sun" viewBox="0 0 140 140" aria-hidden="true">
      <defs>
        <radialGradient id="gl-sun-glow" cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="#FFE7B0" stopOpacity="0.9" /><stop offset="1" stopColor="#FFE7B0" stopOpacity="0" /></radialGradient>
        <radialGradient id="gl-sun-fill" cx="36%" cy="30%" r="78%"><stop offset="0" stopColor="#FFF8DC" /><stop offset="0.55" stopColor="#FFD978" /><stop offset="1" stopColor="#FFB547" /></radialGradient>
      </defs>
      <circle cx="70" cy="70" r="68" fill="url(#gl-sun-glow)" />
      <circle cx="70" cy="70" r="38" fill="url(#gl-sun-fill)" />
      <path d="M52 52c8-10 22-12 30-6" stroke="#fff" strokeWidth="5" strokeLinecap="round" fill="none" opacity="0.6" />
      <circle cx="60" cy="70" r="3.2" fill="#7A5222" /><circle cx="80" cy="70" r="3.2" fill="#7A5222" />
      <path d="M63 80c4 4 10 4 14 0" stroke="#7A5222" strokeWidth="2.6" strokeLinecap="round" fill="none" />
      <circle cx="54" cy="79" r="4.5" fill="#FF9F7A" opacity="0.45" /><circle cx="86" cy="79" r="4.5" fill="#FF9F7A" opacity="0.45" />
    </svg>
  );
}

// stars at fixed pseudo-random spots (same on every render)
const STARS = (() => {
  let seed = 7; const r = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
  return Array.from({ length: 46 }, () => ({ left: `${(r() * 100).toFixed(1)}%`, top: `${(r() * 62).toFixed(1)}%`, delay: `${(-r() * 4).toFixed(2)}s`, scale: (0.6 + r()).toFixed(2) }));
})();

/** The sky behind every glass screen: one gradient per time of day, colour pools for the glass to bend, stars at night. */
export function SkyBackdrop() {
  return (
    <div className="lg-sky" aria-hidden="true">
      <i className="s-morning" /><i className="s-day" /><i className="s-evening" /><i className="s-night" />
      <div className="pools"><b /><b /><b /></div>
      <div className="stars">{STARS.map((s, i) => <b key={i} style={{ left: s.left, top: s.top, animationDelay: s.delay, transform: `scale(${s.scale})` }} />)}</div>
    </div>
  );
}
